import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'antd';

import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import {
  CREATE_LD_CLAIM_FORM_IDS,
  LD_CLAIM_DUPLICATE_STATUS,
  LD_CLAIM_DUPLICATE_STATUS_MESSAGE,
  LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE,
} from '../../../../../contants/ClaimsEnums';
import { APP_ROUTES } from '../../../../../contants/routes';
import {
  setLADClaimEditDrawer,
  setSelectedClaimKey,
} from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { setPageBlocker } from '../../../../../redux/common/CommonActions';
import { validateForms, valueToString } from '../../../../../utilz';
import { routeFileSelector } from '../../../../../utilz/i18n';
import { convertPreClaimsFilesToAPI, LADCovertDatesFromMomentToAPIType } from '../../../../../utilz/PreClaimsUtils';
import EmptyRecords from '../../../../components/Common/EmptyRecords';
import HiddenFormInput from '../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../components/Labels/SecondaryLabel';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import Paper from '../../../../components/Paper/Paper';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import SaveCancelButtons from '../../../Shipment/components/SaveCancelButtons';
import { useGetLADClaimInitialValues } from '../../hooks';
import { useCheckDuplicateClaim, useSaveClaim } from '../hooks';
import CreateLDClaimTabs from './CreateLDClaimTabs';

const LossAndDamageCreateClaimScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const { selectedCompany } = useSelector((state) => state.auth);
  const {
    data: initialCreateData,
    isSuccess: isInitialCreateDataSuccess,
    isFetching: isInitialCreateDataLoading,
  } = useGetLADClaimInitialValues(selectedCompany?.companyId);

  const [activeKeys, setActiveKeys] = useState(['1', '2', '3', '4', '5', '6']);
  const isInitialRenderRef = useRef(true);

  const [items, setItems] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.ITEMS);
  const [notes, setNotes] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.NOTES);
  const [settlements, setSettlements] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.SETTLEMENT);
  const [files, setFiles] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.FLIES);
  const invoiceAndFbNoRef = useRef({
    claimProNo: undefined,
    invoiceNo: undefined,
  });
  const [claimsDetailsForm] = Form.useForm();
  const [claimsFinancialsForm] = Form.useForm();
  const [initialForm] = Form.useForm();
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [isInstantDuplicateValidation, setIsInstantDuplicateValidation] = useState(false);
  const claimFormDataRef = useRef(null);
  const changeCompany = useSelector((state) => state.auth.changeCompany);
  const { mutate: saveClaim } = useSaveClaim();
  const { mutate: checkDuplicateClaim } = useCheckDuplicateClaim();
  const { mutate: checkDuplicateClaimInstant } = useCheckDuplicateClaim({ showLoader: false });

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    if (changeCompany.success && !isInitialRenderRef.current) {
      claimsDetailsForm.resetFields();
      claimsFinancialsForm.resetFields();
      initialForm.resetFields();
      setItems(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.ITEMS);
      setNotes(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.NOTES);
      setSettlements(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.SETTLEMENT);
      setFiles(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.FLIES);
    }
    isInitialRenderRef.current = false;
  }, [changeCompany]);

  const handleFormProviderSubmit = async (submittedForm) => {
    try {
      if (submittedForm === 'saveButtonForm') {
        const formList = [
          validateForms(claimsDetailsForm, 'claimsDetailsForm'),
          validateForms(claimsFinancialsForm, 'claimsFinancialsForm'),
        ];
        await Promise.all(formList);

        if (items?.length === 1 && items?.[0]?.isNew) {
          showNotification({
            message: 'Claim items required minimum one item!',
            type: NOTIFICATION_TYPES.ERROR,
          });
          setActiveKeys(['2']);
          return false;
        }

        const payload = {
          ...claimsDetailsForm.getFieldsValue(),
          ...claimsFinancialsForm.getFieldsValue(),
          claimComment: notes?.filter(({ editMode }) => !editMode),
          claimFile: convertPreClaimsFilesToAPI(files?.filter(({ editMode }) => !editMode)),
          claimItem: items?.filter(({ editMode }) => !editMode),
          claimKey: 0,
          claimSettlement: settlements?.filter(({ editMode }) => !editMode),
        };

        const handleDone = (duplicateStatus) => {
          if (Number(duplicateStatus) === LD_CLAIM_DUPLICATE_STATUS.NEW) {
            initiateCreateClaim(payload);
            return;
          }
          setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
          claimFormDataRef.current = payload;
        };
        const duplicateCheckPayload = {
          claimKey: 0,
          freightBillNo: valueToString(payload.claimProNo),
          invoiceNo: valueToString(payload.invoiceNo),
        };
        checkDuplicateClaim(duplicateCheckPayload, { onSuccess: handleDone });
      }
    } catch (error) {
      setActiveKeys([CREATE_LD_CLAIM_FORM_IDS[error?.formName]]);
    }
  };

  const initiateCreateClaim = (payload) => {
    const handleDone = (newClaimKey) => {
      dispatch(setPageBlocker(false));
      dispatch(setSelectedClaimKey(newClaimKey));
      setTimeout(() => {
        navigate(APP_ROUTES.LOSS_AND_DAMAGE_CLAIMS, {
          state: { showEmailPromptForClaimKey: newClaimKey },
        });
        dispatch(setLADClaimEditDrawer(true));
        // biome-ignore lint/style/noMagicNumbers: TODO: remove timeout
      }, 500);
    };
    const finalPayload = LADCovertDatesFromMomentToAPIType(payload);
    saveClaim(finalPayload, { onSuccess: handleDone });
  };

  const handleDuplicateApprove = () => {
    if (!isInstantDuplicateValidation) {
      initiateCreateClaim(claimFormDataRef.current);
    }
    handleDuplicateCancel();
  };

  const handleDuplicateCancel = () => {
    setDuplicateMessage('');
    setIsInstantDuplicateValidation(false);
    claimFormDataRef.current = null;
  };

  const checkInstantClaimsDuplicate = (changedFieldName) => async () => {
    try {
      const basicDetails = claimsDetailsForm.getFieldsValue();
      if (invoiceAndFbNoRef.current[changedFieldName] === basicDetails[changedFieldName]) {
        return;
        // biome-ignore lint/style/noUselessElse: else part will execute if having different values
      } else {
        invoiceAndFbNoRef.current = {
          claimProNo: basicDetails.claimProNo,
          invoiceNo: basicDetails.invoiceNo,
        };
      }

      const handleDone = (duplicateStatus) => {
        if (Number(duplicateStatus) !== LD_CLAIM_DUPLICATE_STATUS.NEW) {
          setIsInstantDuplicateValidation(true);
          setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
        } else {
          setIsInstantDuplicateValidation(false);
        }
      };
      const payload = {
        claimKey: 0,
        freightBillNo: valueToString(basicDetails.claimProNo),
        invoiceNo: valueToString(basicDetails.invoiceNo),
      };
      checkDuplicateClaimInstant(payload, { onSuccess: handleDone });
    } catch (error) {
      console.log(error);
      setIsInstantDuplicateValidation(false);
    }
  };

  return (
    <>
      <Form.Provider className="overflow-hidden h-100" onFormFinish={handleFormProviderSubmit}>
        <Row className="flex-direction-column flex-nowrap overflow-hidden h-100">
          <Col className="mb-15">
            <Paper className="p-15 border-radius-bottom-none">
              <Row align="middle" justify="space-between">
                <Col>
                  <Form name="initialForm" form={initialForm}>
                    <Row gutter={15}>
                      <Col>
                        <FormLabel>Claim No: </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">0</SecondaryLabel>
                        <HiddenFormInput name="shipmentNo" />
                      </Col>
                      <Col>
                        <FormLabel>Currency: </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">{initialCreateData?.currencyCode}</SecondaryLabel>
                      </Col>
                    </Row>
                  </Form>
                </Col>
                <Col>
                  <Row align="middle" gutter={15}>
                    <Col>
                      <Form name="saveButtonForm">
                        <SaveCancelButtons
                          saveButtonText={t('translation:default.buttons.save')}
                          isNew
                          onCancel={() => navigate(-1)}
                          idSuffix="shipment"
                        />
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Paper>
          </Col>
          <Col className="flex-1 custom-scroll-bar">
            {isInitialCreateDataLoading ? (
              <ContentSpinner size="large" />
            ) : isInitialCreateDataSuccess ? (
              <CreateLDClaimTabs
                activeKeys={activeKeys}
                setActiveKeys={setActiveKeys}
                claimsDetailsForm={claimsDetailsForm}
                items={items}
                setItems={setItems}
                notes={notes}
                setNotes={setNotes}
                settlements={settlements}
                setSettlements={setSettlements}
                files={files}
                setFiles={setFiles}
                claimsFinancialsForm={claimsFinancialsForm}
                checkInstantClaimsDuplicate={checkInstantClaimsDuplicate}
                initValues={initialCreateData}
              />
            ) : (
              <EmptyRecords descriptionOne=" " descriptionTwo=" " title="Failed to load claims page!" />
            )}
          </Col>
        </Row>
      </Form.Provider>
      <ConfirmationModal
        open={Boolean(duplicateMessage)}
        onClose={handleDuplicateCancel}
        onOk={handleDuplicateApprove}
        onCancel={handleDuplicateCancel}
        description={duplicateMessage}
        title={t('translation:default.fields.alert')}
        cancelButton={isInstantDuplicateValidation}
      />
    </>
  );
};

export default LossAndDamageCreateClaimScreen;
